import { useState } from 'react'
import Modal from './Modal.jsx'
import { api } from '../lib/api.js'
import { useToast } from './Toast.jsx'
import { formatDate } from '../lib/dates.js'

/**
 * The stat badge, and the one control that moves it.
 *
 * Wherever somebody is looking at ONE day: the board's day view, its day dialog, and the
 * day a person clicks on the People calendar. Not on a week or month grid as a whole —
 * this is a decision about a single date, and it needs the room to say which.
 *
 * Disabled rather than hidden for a person who cannot fill this board — the house
 * convention, and here it also answers why a badge they can plainly see is not theirs to
 * move.
 *
 * Asymmetric on purpose. Dropping a stat is confirmed, because the scope is not obvious
 * from the button: it covers the whole company and both boards, and people reasonably read
 * it as a standing decision about that holiday rather than about one date. Putting it back
 * is not confirmed — it restores the default, and there is nothing to explain.
 */
export default function HolidayNote({ holiday, date, mode, writable, onChanged }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [asking, setAsking] = useState(false)
  if (!holiday) return null

  async function set(observed) {
    setSaving(true)
    try {
      await api.put(`/api/holidays/${date}`, { mode, observed })
      setAsking(false)
      onChanged()
      toast.success('Saved.')
    } catch (err) {
      toast.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <span className={`holiday-note ${holiday.observed ? '' : 'dismissed'}`}>
      {holiday.observed && <span className="tag stat">STAT</span>}
      <span className="holiday-name">{holiday.name}</span>
      <button
        type="button"
        className="holiday-toggle"
        disabled={saving || !writable}
        title={writable ? undefined : 'Only someone who can fill this board can change this'}
        onClick={() => (holiday.observed ? setAsking(true) : set(true))}
      >
        {holiday.observed ? 'Not a holiday?' : 'Mark as holiday'}
      </button>

      {asking && (
        <Modal title="Mark this day as not a holiday" onClose={() => setAsking(false)} width={520}>
          <p>
            <strong>{holiday.name}</strong> on {formatDate(date, { withYear: true })} will stop
            showing as a statutory holiday on the board.
          </p>
          <p className="hint">
            This is about that one day, not about {holiday.name} in general. Next year’s falls
            on a different date and will be marked as usual.
          </p>
          <div className="modal-actions">
            <button className="btn" type="button" onClick={() => setAsking(false)}>Cancel</button>
            <button className="btn primary" type="button" disabled={saving} onClick={() => set(false)}>
              {saving ? 'Saving…' : 'Mark as not a holiday'}
            </button>
          </div>
        </Modal>
      )}
    </span>
  )
}
